import React, { useState, useEffect, useCallback, useMemo } from "react";
import * as XLSX from "xlsx-js-style";
import {
  X,
  FileSpreadsheet,
  DownloadCloud,
  Loader2,
  AlertCircle,
  Trophy,
  Building2,
  School,
  Users,
  ChevronDown,
  ChevronRight,
  CheckCircle2,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import Select from "react-select";
import { showErrorToast, showSuccessToast } from "../../../components/ConfirmDialog";
import customSelectStyles from "../../../components/ui/customSelectStyles";
import {
  customerInModalSelectStyles,
  formatCustomerOptionLabel,
  filterCustomerOption,
  modalSelectPortalProps,
} from "../../../components/ui/customerSelectInModal";
import {
  classSelectStyles,
  classSelectPortalProps,
  formatClassOptionLabel,
  filterClassOption,
  mapClassesToSelectOptions,
} from "../../../components/ui/classSelectInModal";
import { getAllBranches_Array, getBranchById } from "../../../api/hrm/branches/info";
import HrmDashboardBranchPicker, {
  branchSubtitle,
} from "../../hrm/shared/hrmDashboardBranchSelect";
import {
  getStudentMetaSchools,
  getStudentMetaClasses,
} from "../../../api/elearning/students/students";
import { importResults } from "../../../api/elearning/results/results";
import { fetchClassStudents } from "../../../api/elearning/classes/classes";

function readUserBranchId() {
  try {
    const u = JSON.parse(localStorage.getItem("user") || "{}");
    return u.branch?.id || u.branch_id || null;
  } catch {
    return null;
  }
}

const SESSION_SUPER_ROLES = [
  "super admin",
  "superadmin",
  "administrator",
  "system administrator",
];

function isSuperAdminSession() {
  try {
    const u = JSON.parse(localStorage.getItem("user") || "{}");
    const roles = Array.isArray(u.roles) ? u.roles : [];
    return roles
      .map((r) => String(r || "").trim().toLowerCase())
      .some((r) => SESSION_SUPER_ROLES.includes(r));
  } catch {
    return false;
  }
}

/** Loại điểm UI → import_mode + score_field (backend: 15p & 1 tiết → quiz) */
const SCORE_KIND_OPTIONS = [
  { value: "full", label: "File đầy đủ 6 cột điểm", import_mode: "full", score_field: null },
  { value: "entrance", label: "Kiểm tra đầu vào", import_mode: "single", score_field: "entrance" },
  { value: "midterm_1", label: "Giữa kì 1", import_mode: "single", score_field: "midterm_1" },
  { value: "final_1", label: "Cuối kì 1", import_mode: "single", score_field: "final_1" },
  { value: "midterm_2", label: "Giữa kì 2", import_mode: "single", score_field: "midterm_2" },
  { value: "final_2", label: "Cuối kì 2", import_mode: "single", score_field: "final_2" },
  { value: "exit", label: "Đầu ra", import_mode: "single", score_field: "exit" },
];

const getClassStudentCount = (c) =>
  c?.student_total_count ?? c?.student_active_count ?? c?.student_count ?? c?.total_students;

const formatGenderLabel = (raw) => {
  const g = String(raw || "").trim().toLowerCase();
  if (g === "male" || g === "nam") return "Nam";
  if (g === "female" || g === "nu" || g === "nữ") return "Nữ";
  return "";
};

const formatDateCell = (value) => {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yyyy = d.getFullYear();
  return `${yyyy}-${mm}-${dd}`;
};

const sanitizeFileNamePart = (value, fallback = "") => {
  const text = String(value || "")
    .trim()
    .replace(/[\\/:*?"<>|]+/g, "-")
    .replace(/\s+/g, " ");
  return text || fallback;
};

const readUserBranchName = () => {
  try {
    const u = JSON.parse(localStorage.getItem("user") || "{}");
    return u.branch?.name || u.branch_name || "";
  } catch {
    return "";
  }
};

const splitVietnameseName = (fullName) => {
  const cleaned = String(fullName || "").trim().replace(/\s+/g, " ");
  if (!cleaned) return { ho: "", ten: "" };
  const parts = cleaned.split(" ");
  const ten = parts.pop() || "";
  const ho = parts.join(" ");
  return { ho, ten };
};

const colToLetter = (n) => {
  let s = "";
  let x = n + 1;
  while (x > 0) {
    const m = (x - 1) % 26;
    s = String.fromCharCode(65 + m) + s;
    x = Math.floor((x - m) / 26);
  }
  return s;
};

const thinBorder = {
  top: { style: "thin", color: { rgb: "D1D5DB" } },
  bottom: { style: "thin", color: { rgb: "D1D5DB" } },
  left: { style: "thin", color: { rgb: "D1D5DB" } },
  right: { style: "thin", color: { rgb: "D1D5DB" } },
};

const StepLabel = ({ num, title, active }) => (
  <div className={`flex items-center gap-2 ${active ? "opacity-100" : "opacity-40"}`}>
    <span
      className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold ${
        active ? "bg-red-600 text-white" : "bg-gray-200"
      }`}
    >
      {num}
    </span>
    <span className="text-[11px] font-bold uppercase tracking-wider">{title}</span>
  </div>
);

const ResultImportModal = ({ isOpen, onClose, onRefresh }) => {
  const [step, setStep] = useState(1);
  const [loadingMeta, setLoadingMeta] = useState(false);
  const [loading, setLoading] = useState(false);
  const [file, setFile] = useState(null);
  const [branchId, setBranchId] = useState(null);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [branchAffiliation, setBranchAffiliation] = useState("affiliated");
  const [branchOptions, setBranchOptions] = useState([]);
  const [schools, setSchools] = useState([]);
  const [classes, setClasses] = useState([]);
  const [unit, setUnit] = useState("");
  const [schoolId, setSchoolId] = useState("");
  const [classId, setClassId] = useState("");
  const [scoreKind, setScoreKind] = useState("full");
  const [importErrors, setImportErrors] = useState([]);

  const resetWhenOpen = useCallback(() => {
    setStep(1);
    setUnit("");
    setSchoolId("");
    setClassId("");
    setScoreKind("full");
    setFile(null);
    setClasses([]);
    setSchools([]);
    setImportErrors([]);
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    resetWhenOpen();
    setLoadingMeta(true);
    const init = async () => {
      const superAdmin = isSuperAdminSession();
      setIsSuperAdmin(superAdmin);
      if (superAdmin) {
        try {
          const rows = await getAllBranches_Array({ is_active: true, limit: 500 });
          const opts = (rows || []).map((b) => ({
            value: b.id,
            label: b.name,
            affiliation_type: b.affiliation_type || "affiliated",
            manager: b.manager || "",
          }));
          setBranchOptions(opts);
          setBranchId(null);
          setBranchAffiliation("affiliated");
          setUnit("");
        } catch {
          setBranchOptions([]);
        } finally {
          setLoadingMeta(false);
        }
        return;
      }

      const bid = readUserBranchId();
      setBranchId(bid);
      if (!bid) {
        setLoadingMeta(false);
        return;
      }
      try {
        const br = await getBranchById(bid);
        const aff = br?.affiliation_type || "affiliated";
        setBranchAffiliation(aff);
        setUnit(aff === "franchise" ? "center" : "");
      } catch {
        setBranchAffiliation("affiliated");
      } finally {
        setLoadingMeta(false);
      }
    };
    init();
  }, [isOpen, resetWhenOpen]);

  useEffect(() => {
    if (!isOpen) return;
    if (!branchId || unit !== "school" || !isSuperAdmin) {
      setSchools([]);
      return;
    }
    setLoadingMeta(true);
    getStudentMetaSchools(branchId)
      .then((d) => setSchools(d.schools || []))
      .catch(() => setSchools([]))
      .finally(() => setLoadingMeta(false));
  }, [isOpen, branchId, unit, isSuperAdmin]);

  const loadClasses = useCallback(async () => {
    if (isSuperAdmin && !branchId) return;
    if (!unit) {
      setClasses([]);
      return;
    }
    setLoadingMeta(true);
    try {
      const params = { unit: unit === "center" ? "center" : "school" };
      if (isSuperAdmin && branchId) {
        params.branch_id = branchId;
        if (unit === "school" && schoolId) params.school_id = schoolId;
      }
      const d = await getStudentMetaClasses(params);
      const incomingClasses = Array.isArray(d.classes) ? d.classes : [];

      // Khi đã chọn đối tác (mode trường học), chỉ giữ lớp có sĩ số > 0.
      if (unit === "school" && schoolId) {
        const filtered = incomingClasses.filter((c) => Number(getClassStudentCount(c) || 0) > 0);
        setClasses(filtered);
      } else {
        setClasses(incomingClasses);
      }
    } catch {
      setClasses([]);
      showErrorToast("Không tải được danh sách lớp.");
    } finally {
      setLoadingMeta(false);
    }
  }, [branchId, unit, schoolId, isSuperAdmin]);

  useEffect(() => {
    if (!isOpen) return;
    if (isSuperAdmin && !branchId) {
      setClasses([]);
      setClassId("");
      return;
    }
    if (!unit) {
      setClasses([]);
      setClassId("");
      return;
    }
    loadClasses();
  }, [isOpen, branchId, unit, schoolId, isSuperAdmin, loadClasses]);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile && (selectedFile.name.endsWith(".xlsx") || selectedFile.name.endsWith(".xls"))) {
      setFile(selectedFile);
      setImportErrors([]);
    } else {
      showErrorToast("Vui lòng chọn file Excel (.xlsx hoặc .xls)");
    }
  };

  const selectedScore = SCORE_KIND_OPTIONS.find((o) => o.value === scoreKind) || SCORE_KIND_OPTIONS[0];

  const handleDownloadTemplate = async () => {
    if (!classId) {
      showErrorToast("Vui lòng chọn lớp trước khi tải biểu mẫu.");
      return;
    }
    setLoadingMeta(true);
    try {
      const studentRes = await fetchClassStudents(classId);
      const classStudents = Array.isArray(studentRes?.students) ? studentRes.students : [];
      if (!classStudents.length) {
        showErrorToast("Lớp chưa có học viên để tạo biểu mẫu nhập điểm.");
        return;
      }

      const baseHeaders = ["ID", "Mã học sinh", "Họ", "Tên", "Ngày sinh", "Giới tính"];
      let scoreHeaders = [];
      if (selectedScore.import_mode === "full") {
        scoreHeaders = ["Đầu vào", "Giữa kì 1", "Cuối kì 1", "Giữa kì 2", "Cuối kì 2", "Đầu ra", "Đánh giá", "Kết quả"];
      } else {
        scoreHeaders = ["Điểm", "Đánh giá", "Kết quả"];
      }

      const finalHeaders = [...baseHeaders, ...scoreHeaders];
      const sortedStudents = [...classStudents].sort((a, b) => {
        const aName = splitVietnameseName(a.full_name);
        const bName = splitVietnameseName(b.full_name);
        const byTen = aName.ten.localeCompare(bName.ten, "vi", { sensitivity: "base" });
        if (byTen !== 0) return byTen;
        const byHo = aName.ho.localeCompare(bName.ho, "vi", { sensitivity: "base" });
        if (byHo !== 0) return byHo;
        return String(a.student_id || "").localeCompare(String(b.student_id || ""), "vi", { sensitivity: "base" });
      });

      const rows = [
        finalHeaders,
        ...sortedStudents.map((s) => {
          const scoreCols = new Array(scoreHeaders.length).fill("");
          const nameParts = splitVietnameseName(s.full_name);
          return [
            s.student_id || "",
            s.student_code || "",
            nameParts.ho,
            nameParts.ten,
            formatDateCell(s.dob),
            formatGenderLabel(s.gender),
            ...scoreCols,
          ];
        }),
      ];

      const ws = XLSX.utils.aoa_to_sheet([]);
      const title = `BIỂU MẪU NHẬP ĐIỂM - ${selectedScore.label.toUpperCase()}`;
      const subtitle = `Lớp: ${classOptions.find((c) => c.value === classId)?.label || classId} | Số học viên: ${classStudents.length}`;
      const instruction = "Vùng nền vàng là ô cần nhập liệu. Không chỉnh sửa các cột thông tin học viên.";
      const headerStartRow = 5; // 1-based
      const dataStartRow = headerStartRow + 1;
      const totalRows = rows.length;
      const lastCol = colToLetter(finalHeaders.length - 1);
      XLSX.utils.sheet_add_aoa(ws, [[title]], { origin: "A1" });
      XLSX.utils.sheet_add_aoa(ws, [[subtitle]], { origin: "A2" });
      XLSX.utils.sheet_add_aoa(ws, [[instruction]], { origin: "A3" });
      XLSX.utils.sheet_add_aoa(ws, rows, { origin: `A${headerStartRow}` });
      ws["!merges"] = [
        { s: { r: 0, c: 0 }, e: { r: 0, c: finalHeaders.length - 1 } },
        { s: { r: 1, c: 0 }, e: { r: 1, c: finalHeaders.length - 1 } },
        { s: { r: 2, c: 0 }, e: { r: 2, c: finalHeaders.length - 1 } },
      ];
      ws["!autofilter"] = { ref: `A${headerStartRow}:${lastCol}${headerStartRow}` };
      ws["!cols"] = [
        { wch: 38 }, // ID
        { wch: 14 }, // Mã HS
        { wch: 20 }, // Họ
        { wch: 14 }, // Tên
        { wch: 14 }, // Ngày sinh
        { wch: 12 }, // Giới tính
        ...scoreHeaders.map((h) => ({ wch: h === "Đánh giá" || h === "Kết quả" ? 30 : 10 })),
      ];

      // Tiêu đề
      const titleCell = ws["A1"];
      if (titleCell) {
        titleCell.s = {
          font: { bold: true, sz: 14, color: { rgb: "7F1D1D" } },
          fill: { fgColor: { rgb: "FEE2E2" } },
          alignment: { horizontal: "center", vertical: "center" },
          border: thinBorder,
        };
      }
      const subtitleCell = ws["A2"];
      if (subtitleCell) {
        subtitleCell.s = {
          font: { bold: true, color: { rgb: "374151" } },
          fill: { fgColor: { rgb: "F9FAFB" } },
          alignment: { horizontal: "left", vertical: "center" },
          border: thinBorder,
        };
      }
      const instructionCell = ws["A3"];
      if (instructionCell) {
        instructionCell.s = {
          font: { italic: true, color: { rgb: "92400E" } },
          fill: { fgColor: { rgb: "FEF3C7" } },
          alignment: { horizontal: "left", vertical: "center", wrapText: true },
          border: thinBorder,
        };
      }

      // Header row style
      for (let c = 0; c < finalHeaders.length; c += 1) {
        const addr = `${colToLetter(c)}${headerStartRow}`;
        if (ws[addr]) {
          ws[addr].s = {
            font: { bold: true, color: { rgb: "FFFFFF" } },
            fill: { fgColor: { rgb: "B91C1C" } },
            alignment: { horizontal: "center", vertical: "center", wrapText: true },
            border: thinBorder,
          };
        }
      }

      // Data rows style: khóa cột info và tô vùng nhập
      const readonlyCols = baseHeaders.length;
      for (let r = dataStartRow; r < dataStartRow + totalRows - 1; r += 1) {
        for (let c = 0; c < finalHeaders.length; c += 1) {
          const addr = `${colToLetter(c)}${r}`;
          if (!ws[addr]) continue;
          const isReadonly = c < readonlyCols;
          ws[addr].s = {
            border: thinBorder,
            alignment: { vertical: "center", wrapText: true },
            fill: { fgColor: { rgb: isReadonly ? "F3F4F6" : "FEF9C3" } },
            font: { color: { rgb: "111827" } },
          };
        }
      }

      const guideRows = [
        ["HƯỚNG DẪN NHẬP ĐIỂM"],
        ["1) Không sửa 6 cột đầu: ID, Mã học sinh, Họ, Tên, Ngày sinh, Giới tính."],
        ["2) Chỉ nhập dữ liệu ở các cột nền vàng."],
        [
          selectedScore.value === "exit"
            ? "3) Với loại điểm Đầu ra, cột Điểm có thể để trống. Nếu nhập điểm thì điểm phải trong khoảng 0-10."
            : "3) Điểm nhập trong khoảng 0-10. Có thể dùng số thập phân (ví dụ: 8.5).",
        ],
        ["4) Bắt buộc nhập đầy đủ cột Đánh giá và Kết quả cho mọi loại file nhập điểm."],
        [
          "5) Cột Kết quả là nội dung in lên giấy chứng nhận (ví dụ: Hoàn thành xuất sắc khoá học Tiếng Anh Tăng Cường năm học 2025 - 2026).",
        ],
      ];
      const wsGuide = XLSX.utils.aoa_to_sheet(guideRows);
      wsGuide["!cols"] = [{ wch: 120 }];

      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Nhap diem");
      XLSX.utils.book_append_sheet(wb, wsGuide, "Huong dan");
      XLSX.writeFile(wb, buildStudentListFileName());
      showSuccessToast(`Đã tạo biểu mẫu theo lớp (${classStudents.length} học viên).`);
    } catch (err) {
      showErrorToast(err?.response?.data?.message || "Không thể tạo biểu mẫu theo lớp.");
    } finally {
      setLoadingMeta(false);
    }
  };

  const handleUpload = async () => {
    if (!branchId) {
      showErrorToast("Tài khoản chưa gắn chi nhánh.");
      return;
    }
    if (!classId) {
      showErrorToast("Vui lòng chọn lớp học");
      return;
    }
    if (!file) {
      showErrorToast("Vui lòng chọn file Excel (.xlsx hoặc .xls)");
      return;
    }
    setLoading(true);
    try {
      const res = await importResults(file, {
        class_id: classId,
        import_mode: selectedScore.import_mode,
        score_field: selectedScore.score_field || undefined,
      });
      showSuccessToast(res.message || `Import thành công ${res.imported} bản ghi`);
      if (Array.isArray(res.errors) && res.errors.length > 0) {
        const toText = (e) => {
          if (typeof e === "string") return e;
          const line = e?.line != null ? `Dòng ${e.line}` : "Lỗi dữ liệu";
          const msg = e?.message || "Dữ liệu không hợp lệ";
          return `${line}: ${msg}`;
        };
        const normalized = res.errors.map(toText);
        setImportErrors(normalized);
        const preview = normalized.slice(0, 5).join(" | ");
        const suffix =
          normalized.length > 5 ? ` | ... và ${normalized.length - 5} lỗi khác.` : "";
        showErrorToast(`Có ${normalized.length} dòng lỗi. ${preview}${suffix}`);
      } else {
        setImportErrors([]);
      }
      onRefresh?.();
      if (!Array.isArray(res.errors) || res.errors.length === 0) {
        onClose();
        resetWhenOpen();
      }
    } catch (err) {
      const errMsg = err.response?.data?.message || "Lỗi khi tải lên dữ liệu điểm";
      setImportErrors([errMsg]);
      showErrorToast(errMsg);
    } finally {
      setLoading(false);
    }
  };

  const openGuide = () => {
    const base = process.env.PUBLIC_URL || "";
    window.open(`${base}/guides/elearning-results-guide.html`, "_blank", "noopener,noreferrer");
  };

  const canProceedStep1 = Boolean(
    branchId &&
      unit &&
      classId &&
      (unit === "center" || (unit === "school" && (isSuperAdmin ? schoolId : true)))
  );
  const step1NextDisabled = !canProceedStep1 || loadingMeta;
  const isFranchiseBranch = branchAffiliation === "franchise";
  const canPickUnit = isSuperAdmin || (!isSuperAdmin && !isFranchiseBranch);
  const showSchoolSelect = isSuperAdmin && unit === "school";
  const schoolOptions = useMemo(
    () =>
      schools.map((s) => ({
        value: s.id,
        label: s.name,
        ward: s.ward || s.ward_name || "",
      })),
    [schools]
  );
  const classOptions = useMemo(
    () => mapClassesToSelectOptions(classes),
    [classes]
  );

  const buildStudentListFileName = useCallback(() => {
    const branchName = isSuperAdmin
      ? branchOptions.find((b) => b.value === branchId)?.label || ""
      : readUserBranchName();

    const selectedClass = classes.find((c) => String(c.id) === String(classId));
    const className = selectedClass?.name || "";

    let schoolName = "";
    if (unit === "center") {
      schoolName = "Trung tâm";
    } else if (unit === "school") {
      schoolName =
        schools.find((s) => String(s.id) === String(schoolId))?.name ||
        selectedClass?.school_name ||
        "";
    }

    const parts = [
      sanitizeFileNamePart(branchName, "Chi nhánh"),
      sanitizeFileNamePart(schoolName, "Trường"),
      sanitizeFileNamePart(className, "Lớp"),
    ];
    return `${parts.join(" - ")}.xlsx`;
  }, [branchId, branchOptions, classId, classes, isSuperAdmin, schoolId, schools, unit]);

  const branchSelectOptions = useMemo(
    () => [
      { value: "", label: "Chọn chi nhánh", sub: "Tên chi nhánh • Loại chi nhánh • Người đứng đầu" },
      ...branchOptions.map((b) => ({ value: b.value, label: b.label, sub: branchSubtitle(b) })),
    ],
    [branchOptions]
  );
  const schoolSignal = useMemo(
    () => ({
      matchedSchoolCount: schoolOptions.length,
      branchTotalCount: schoolOptions.length,
    }),
    [schoolOptions]
  );
  const classSignal = useMemo(() => {
    const total = classOptions.length;
    if (unit === "school") {
      const selectedSchool = schoolOptions.find((s) => s.value === schoolId);
      return selectedSchool
        ? `Lớp học khớp với đối tác "${selectedSchool.label}": ${total}`
        : `Lớp học khớp theo điều kiện hiện tại: ${total}`;
    }
    return `Lớp học tại trung tâm khớp điều kiện: ${total}`;
  }, [classOptions, unit, schoolId, schoolOptions]);

  const footerPrimary = () => {
    if (step === 1) {
      return (
        <button
          type="button"
          onClick={() => setStep(2)}
          disabled={step1NextDisabled}
          className="px-10 py-3 bg-red-600 text-white rounded-2xl font-bold flex items-center gap-2 shadow-lg shadow-red-100 hover:bg-red-700 transition-all active:scale-95 disabled:opacity-50 uppercase text-[11px] tracking-widest"
        >
          Tiếp theo
        </button>
      );
    }
    if (step === 2) {
      return (
        <button
          type="button"
          onClick={() => setStep(3)}
          className="px-10 py-3 bg-red-600 text-white rounded-2xl font-bold flex items-center gap-2 shadow-lg shadow-red-100 hover:bg-red-700 transition-all active:scale-95 uppercase text-[11px] tracking-widest"
        >
          Tiếp theo
        </button>
      );
    }
    return (
      <button
        type="button"
        onClick={handleUpload}
        disabled={loading || loadingMeta || !file}
        className="px-10 py-3 bg-red-600 text-white rounded-2xl font-black flex items-center gap-2 shadow-lg shadow-red-100 hover:bg-red-700 transition-all active:scale-95 disabled:opacity-50 uppercase text-[11px] tracking-widest"
      >
        {loading ? <Loader2 className="animate-spin" size={18} /> : null}
        {loading ? "Đang import…" : "Import điểm"}
      </button>
    );
  };

  const footerLeft = () => {
    if (step === 1) {
      return (
        <button
          type="button"
          onClick={onClose}
          className="px-6 py-3 font-bold text-gray-500 hover:bg-gray-200 rounded-2xl transition-all uppercase text-[11px] tracking-widest"
        >
          Huỷ bỏ
        </button>
      );
    }
    return (
      <button
        type="button"
        onClick={() => setStep((s) => Math.max(1, s - 1))}
        className="px-6 py-3 font-bold text-gray-500 hover:bg-gray-200 rounded-2xl transition-all uppercase text-[11px] tracking-widest"
      >
        Quay lại
      </button>
    );
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-[110]"
          />
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            className="fixed inset-0 flex items-center justify-center z-[120] p-4 pointer-events-none"
          >
            <div className="bg-white w-full max-w-2xl rounded-3xl shadow-2xl overflow-hidden pointer-events-auto flex flex-col max-h-[92vh]">
              <div className="p-6 md:p-8 border-b border-gray-100 flex justify-between items-center bg-gray-50/50 shrink-0">
                <div>
                  <h2 className="text-xl md:text-2xl font-black text-gray-800 flex items-center gap-3 uppercase tracking-tight">
                    <Trophy className="text-red-600 shrink-0" /> Nhập điểm
                  </h2>
                  <p className="text-xs text-gray-500 font-medium mt-1">
                    Cấu hình theo phạm vi tài khoản: chi nhánh / đối tượng / đối tác / lớp
                  </p>
                </div>
                <button
                  type="button"
                  onClick={onClose}
                  className="p-2 hover:bg-white rounded-full transition-all shadow-sm border border-transparent hover:border-gray-200"
                >
                  <X size={20} />
                </button>
              </div>

              <div className="px-6 md:px-8 py-4 bg-white border-b border-gray-50 flex flex-wrap items-center justify-between gap-2 shrink-0">
                <StepLabel num={1} title="Đơn vị & lớp" active={step === 1} />
                <ChevronRight size={14} className="text-gray-300 hidden sm:block" />
                <StepLabel num={2} title="Loại điểm" active={step === 2} />
                <ChevronRight size={14} className="text-gray-300 hidden sm:block" />
                <StepLabel num={3} title="Tải file" active={step === 3} />
              </div>

              <div className="p-6 md:p-8 overflow-y-auto max-h-[min(56vh,480px)] custom-scrollbar bg-slate-50/30 text-left">
                {step === 1 && (
                  <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
                    {!isSuperAdmin && !branchId && (
                      <div className="p-4 bg-amber-50 border border-amber-100 rounded-2xl text-sm text-amber-800 font-bold">
                        Tài khoản chưa gắn chi nhánh — không thể tải đơn vị / lớp. Vui lòng liên hệ quản trị.
                      </div>
                    )}
                    {isSuperAdmin && !branchId && (
                      <div className="p-4 bg-blue-50 border border-blue-100 rounded-2xl text-sm text-blue-800 font-bold">
                        Vui lòng chọn chi nhánh để tải danh sách đối tượng và lớp học.
                      </div>
                    )}

                    {isSuperAdmin && (
                      <div className="space-y-2">
                        <label className="text-[11px] font-black text-gray-400 uppercase tracking-widest">
                          Chi nhánh
                        </label>
                        <HrmDashboardBranchPicker
                          instanceId="result-import-branch"
                          options={branchSelectOptions}
                          value={branchId || ""}
                          onChange={(nextVal) => {
                            const nextBranchId = nextVal || null;
                            const picked = branchOptions.find((b) => b.value === nextBranchId);
                            const aff = picked?.affiliation_type || "affiliated";
                            setBranchId(nextBranchId);
                            setBranchAffiliation(aff);
                            setUnit(aff === "franchise" ? "center" : "");
                            setSchoolId("");
                            setClassId("");
                            setClasses([]);
                          }}
                          aria-label="Chọn chi nhánh nhập điểm"
                        />
                      </div>
                    )}

                    <div className="space-y-2">
                      <label className="text-[11px] font-black text-gray-400 uppercase tracking-widest">
                        Đối tượng
                      </label>
                      {canPickUnit ? (
                        <div className="grid grid-cols-2 gap-3 pt-1">
                          <button
                            type="button"
                            onClick={() => {
                              setUnit("school");
                              setClassId("");
                              setSchoolId("");
                              setClasses([]);
                            }}
                            className={`flex flex-col items-center p-3 rounded-2xl border-2 transition-all ${
                              unit === "school"
                                ? "border-red-600 bg-red-50 text-red-700"
                                : "border-gray-100 bg-white text-gray-500 hover:border-gray-300"
                            }`}
                            disabled={isFranchiseBranch}
                          >
                            <span className="text-xl mb-1">🏫</span>
                            <span className="text-[11px] font-black uppercase tracking-wider">
                              Tại trường học
                            </span>
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              setUnit("center");
                              setSchoolId("");
                              setClassId("");
                              setClasses([]);
                            }}
                            className={`flex flex-col items-center p-3 rounded-2xl border-2 transition-all ${
                              unit === "center"
                                ? "border-red-600 bg-red-50 text-red-700"
                                : "border-gray-100 bg-white text-gray-500 hover:border-gray-300"
                            }`}
                          >
                            <span className="text-xl mb-1">🏛️</span>
                            <span className="text-[11px] font-black uppercase tracking-wider">
                              Tại trung tâm
                            </span>
                          </button>
                        </div>
                      ) : (
                        <button
                          type="button"
                          className="w-full flex flex-col items-center p-3 rounded-2xl border-2 border-red-600 bg-red-50 text-red-700"
                          disabled
                        >
                          <span className="text-xl mb-1">🏛️</span>
                          <span className="text-[11px] font-black uppercase tracking-wider">
                            Tại trung tâm
                          </span>
                        </button>
                      )}
                      {!unit ? (
                        <p className="text-[10px] font-semibold text-amber-700">
                          Vui lòng chọn đối tượng để tải danh sách lớp học.
                        </p>
                      ) : null}
                    </div>

                    {showSchoolSelect && (
                      <div className="space-y-2">
                        <label className="text-[11px] font-black text-gray-400 uppercase tracking-widest">
                          Chọn đối tác
                        </label>
                        <Select
                          options={schoolOptions}
                          value={schoolOptions.find((opt) => opt.value === schoolId) || null}
                          onChange={(opt) => {
                            setSchoolId(opt?.value || "");
                            setClassId("");
                          }}
                          placeholder="Chọn đối tác trường học *"
                          styles={customerInModalSelectStyles}
                          formatOptionLabel={formatCustomerOptionLabel}
                          filterOption={filterCustomerOption}
                          {...modalSelectPortalProps}
                          isDisabled={!branchId}
                        />
                        <p className="mt-1 text-[10px] font-semibold tracking-wide text-gray-500">
                          {`Khớp loại trường: ${schoolSignal.matchedSchoolCount} | Tổng đối tác chi nhánh: ${schoolSignal.branchTotalCount}`}
                        </p>
                      </div>
                    )}

                    <div className="space-y-2">
                      <label className="text-[11px] font-black text-gray-400 uppercase tracking-widest flex items-center gap-2">
                        <Users size={14} /> Lớp học
                      </label>
                      <Select
                        instanceId="result-import-class"
                        styles={classSelectStyles}
                        options={classOptions}
                        value={classOptions.find((o) => o.value === classId) || null}
                        onChange={(opt) => setClassId(opt?.value || "")}
                        placeholder="Tìm hoặc chọn lớp học *"
                        isClearable={false}
                        isSearchable
                        filterOption={filterClassOption}
                        formatOptionLabel={formatClassOptionLabel}
                        noOptionsMessage={({ inputValue }) =>
                          inputValue
                            ? "Không tìm thấy lớp phù hợp"
                            : "Không có lớp trong điều kiện hiện tại"
                        }
                        maxMenuHeight={280}
                        menuPlacement="auto"
                        isDisabled={
                          (isSuperAdmin && (!branchId || (showSchoolSelect && !schoolId)))
                          || loadingMeta
                        }
                        {...classSelectPortalProps}
                      />
                      <p className="mt-1 text-[10px] font-semibold tracking-wide text-gray-500">
                        {classSignal}
                      </p>
                    </div>

                    <div className="p-4 bg-blue-50 border border-blue-100 rounded-2xl flex gap-3">
                      <AlertCircle className="text-blue-500 shrink-0" size={18} />
                      <p className="text-[11px] text-blue-700 leading-relaxed font-medium">
                        Chọn đúng <strong>đơn vị</strong> và <strong>lớp</strong> để điểm được ghi vào lớp tương ứng. Lớp chỉ hiện sau khi chọn trường/đối tác (nếu là Trường / đối tác).
                      </p>
                    </div>
                  </div>
                )}

                {step === 2 && (
                  <div className="space-y-6 animate-in fade-in slide-in-from-right-4 duration-500">
                    <div className="space-y-2">
                      <label className="text-[11px] font-black text-gray-400 uppercase tracking-widest">Loại điểm</label>
                      <Select
                        styles={customSelectStyles}
                        options={SCORE_KIND_OPTIONS}
                        value={SCORE_KIND_OPTIONS.find((o) => o.value === scoreKind) || null}
                        onChange={(opt) => setScoreKind(opt?.value || "full")}
                        placeholder="Chọn loại điểm *"
                        isClearable={false}
                        menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                      />
                    </div>
                    <div className="p-4 bg-slate-50 border border-slate-100 rounded-2xl text-[11px] text-slate-600 leading-relaxed">
                      <p className="font-bold text-slate-700 uppercase tracking-wide mb-1">Gợi ý</p>
                      <p>
                        Biểu mẫu sẽ lấy <strong>danh sách học viên thực tế của lớp đã chọn</strong> (ID, mã học sinh, họ tên, ngày sinh, giới tính). Mọi loại file đều có cột <strong>Đánh giá</strong> và <strong>Kết quả</strong>; cột Kết quả dùng để in giấy chứng nhận.
                      </p>
                    </div>
                  </div>
                )}

                {step === 3 && (
                  <div className="space-y-6 text-center py-2 animate-in fade-in slide-in-from-right-4 duration-500">
                    <div className="border-2 border-dashed border-gray-200 rounded-[2rem] p-10 hover:border-red-400 hover:bg-red-50/30 transition-all group relative bg-white">
                      <input
                        type="file"
                        accept=".xlsx,.xls"
                        className="absolute inset-0 opacity-0 cursor-pointer z-10"
                        onChange={handleFileChange}
                      />
                      <div className="space-y-4">
                        <div className="w-16 h-16 bg-red-100 text-red-600 rounded-2xl flex items-center justify-center mx-auto group-hover:scale-110 transition-transform">
                          {file ? <CheckCircle2 size={32} /> : <FileSpreadsheet size={32} />}
                        </div>
                        <div>
                          <p className="text-sm font-bold text-gray-800">
                            {file ? file.name : "Kéo thả file vào đây hoặc click để chọn"}
                          </p>
                          <p className="text-xs text-gray-400 mt-1">.xlsx, .xls</p>
                        </div>
                      </div>
                    </div>

                    <div className="flex flex-col sm:flex-row gap-3">
                      <button
                        type="button"
                        onClick={handleDownloadTemplate}
                        className="flex-1 flex items-center justify-center gap-2 py-3 rounded-2xl border border-red-100 bg-red-50/50 text-red-700 text-xs font-bold uppercase tracking-wide hover:bg-red-50 transition-all"
                      >
                        <DownloadCloud size={16} />
                        Tải danh sách học viên
                      </button>
                      <button
                        type="button"
                        onClick={openGuide}
                        className="flex-1 flex items-center justify-center gap-2 py-3 rounded-2xl border border-slate-200 bg-slate-50 text-slate-700 text-xs font-bold uppercase tracking-wide hover:bg-slate-100 transition-all"
                      >
                        Hướng dẫn nhập liệu
                      </button>
                    </div>

                    <div className="p-5 bg-blue-50 border border-blue-100 rounded-[1.5rem] flex gap-4 text-left">
                      <AlertCircle className="text-blue-500 shrink-0" size={20} />
                      <div className="text-[11px] text-blue-800 leading-relaxed font-semibold space-y-1">
                        <p className="font-black uppercase tracking-tight">Gợi ý nhanh</p>
                        {selectedScore.import_mode === "full" ? (
                          <p>
                            Cột điểm gồm <code className="bg-white/80 px-1 rounded">Đầu vào</code>,{" "}
                            <code className="bg-white/80 px-1 rounded">Giữa kì 1</code>, <code className="bg-white/80 px-1 rounded">Cuối kì 1</code>,{" "}
                            <code className="bg-white/80 px-1 rounded">Giữa kì 2</code>, <code className="bg-white/80 px-1 rounded">Cuối kì 2</code>,{" "}
                            <code className="bg-white/80 px-1 rounded">Đầu ra</code>, <code className="bg-white/80 px-1 rounded">Đánh giá</code> và{" "}
                            <code className="bg-white/80 px-1 rounded">Kết quả</code>.
                          </p>
                        ) : (
                          <p>
                            Một cột điểm: <code className="bg-white/80 px-1 rounded">Điểm</code> +{" "}
                            <code className="bg-white/80 px-1 rounded">Đánh giá</code> +{" "}
                            <code className="bg-white/80 px-1 rounded">Kết quả</code>.
                            {scoreKind === "exit" ? (
                              <>
                                {" "}Riêng <code className="bg-white/80 px-1 rounded">Đầu ra</code>, có thể để trống <code className="bg-white/80 px-1 rounded">Điểm</code> nếu đã nhập đủ <code className="bg-white/80 px-1 rounded">Đánh giá</code> và <code className="bg-white/80 px-1 rounded">Kết quả</code>.
                              </>
                            ) : null}
                          </p>
                        )}
                      </div>
                    </div>

                    {importErrors.length > 0 && (
                      <div className="rounded-2xl border border-rose-200 bg-rose-50/80 p-4 text-left">
                        <div className="flex items-center justify-between gap-3">
                          <p className="text-[11px] font-bold text-rose-700 uppercase tracking-wide">
                            Chi tiết lỗi import điểm
                          </p>
                          <span className="text-[11px] font-semibold text-rose-600">
                            {importErrors.length} lỗi
                          </span>
                        </div>
                        <p className="text-[11px] text-rose-700 mt-1">
                          Hệ thống giữ nguyên màn hình để bạn kiểm tra và sửa file trước khi import lại.
                        </p>
                        <div className="mt-3 max-h-44 overflow-y-auto rounded-xl border border-rose-100 bg-white p-3">
                          <ul className="space-y-1.5 text-[11px] text-rose-800 leading-relaxed">
                            {importErrors.map((line, idx) => (
                              <li key={`${idx}-${line}`}>- {line}</li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div className="p-6 md:p-8 bg-gray-50 border-t border-gray-100 flex justify-between items-center shrink-0">
                {footerLeft()}
                {footerPrimary()}
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default ResultImportModal;
